<template>
  <b-container fluid>
    <b-row>
      <b-col md="12">
        <iq-card class-name="tooth-list">
          <template v-slot:headerTitle>
            <h4 class="card-title mb-0">{{ $t('ToothTreatments') }}</h4>
          </template>
          <template v-slot:headerAction>
            <b-button v-if="canCreate" variant="primary" size="sm" :to="{ name: 'toothTreatment.new', params: { customerId: customerId } }">
              <i class="fa fa-plus mr-1" aria-hidden="true"></i>{{ $t('NewToothTreatment') }}
            </b-button>
          </template>
          <template v-slot:body>
            <Loader v-if="isLoading" :is-visible="isLoading" />

            <!-- Anahtarlar: FontAwesome <i> -> <svg> değişimi yüzünden div'ler birbirine yamanmamalı. -->
            <div v-else-if="treatments.length === 0" key="empty" class="tooth-list__empty">
              <span class="tooth-list__empty-icon"><i class="fa-solid fa-tooth" aria-hidden="true"></i></span>
              <p class="mb-3">{{ $t('NoData') }}</p>
              <b-button v-if="canCreate" variant="outline-primary" size="sm" :to="{ name: 'toothTreatment.new', params: { customerId: customerId } }">
                <i class="fa fa-plus mr-1" aria-hidden="true"></i>{{ $t('NewToothTreatment') }}
              </b-button>
            </div>

            <div v-else key="items" class="tooth-list__items">
              <article v-for="treatment in treatments" :key="treatment.id"
                       class="tooth-list__item"
                       :class="{ 'tooth-list__item--clickable': canEdit }"
                       @click="openTreatment(treatment)">
                <header class="tooth-list__item-head">
                  <div class="tooth-list__meta">
                    <span class="tooth-list__date"><i class="fa-regular fa-calendar mr-1" aria-hidden="true"></i>{{ formatDate(treatment.date) }}</span>
                    <span class="badge badge-pill" :class="treatment.isPrimary ? 'iq-bg-warning' : 'iq-bg-primary'">
                      {{ $t(treatment.isPrimary ? 'PrimaryTeeth' : 'PermanentTeeth') }}
                    </span>
                    <span v-if="treatment.branchName" class="tooth-list__muted"><i class="fa fa-building mr-1" aria-hidden="true"></i>{{ treatment.branchName }}</span>
                    <span v-if="treatment.staffNames.length" class="tooth-list__muted"><i class="fa fa-user-md mr-1" aria-hidden="true"></i>{{ treatment.staffNames.join(', ') }}</span>
                  </div>
                  <div class="tooth-list__actions" @click.stop>
                    <b-button v-if="canEdit" variant="link" size="sm" :title="$t('Edit')" :aria-label="$t('Edit')" @click="openTreatment(treatment)">
                      <i class="fa-regular fa-pen-to-square" aria-hidden="true"></i>
                    </b-button>
                    <b-button v-if="canDownload" variant="link" size="sm" :title="$t('Download')" :aria-label="$t('Download')" @click="download(treatment)">
                      <i class="fa-regular fa-file-pdf" aria-hidden="true"></i>
                    </b-button>
                    <b-button v-if="canDelete" variant="link" size="sm" class="text-danger" :title="$t('Delete')" :aria-label="$t('Delete')" @click="confirmDelete(treatment)">
                      <i class="fa-regular fa-trash-can" aria-hidden="true"></i>
                    </b-button>
                  </div>
                </header>

                <ul class="tooth-list__groups">
                  <li v-for="(group, index) in treatment.groups" :key="index" class="tooth-list__group">
                    <span class="tooth-list__chips">
                      <span v-for="(toothNumber, chipIndex) in group.toothNumbers" :key="toothNumber + '-' + chipIndex" class="tooth-chip">{{ toothNumber }}</span>
                    </span>
                    <span class="tooth-list__entity">
                      {{ group.entityName }}
                      <small class="tooth-list__muted">{{ group.quantityPerTooth != null ? 'x' + formatQuantity(group.quantityPerTooth) : formatQuantity(group.totalQuantity) }}</small>
                    </span>
                    <span v-if="showPricing && group.totalAmount" class="tooth-list__amount">{{ formatAmount(group.totalAmount) }} {{ group.currencySymbol }}</span>
                  </li>
                </ul>

                <footer v-if="treatment.notes || (showPricing && treatment.totals.length)" class="tooth-list__item-foot">
                  <span v-if="treatment.notes" class="tooth-list__note"><i class="fa-regular fa-note-sticky mr-1" aria-hidden="true"></i>{{ treatment.notes }}</span>
                  <span v-if="showPricing && treatment.totals.length" class="tooth-list__totals">
                    <span class="tooth-list__muted">{{ $t('TreatmentPrice') }}</span>
                    <strong v-for="total in treatment.totals" :key="total.currencyId">{{ formatAmount(total.amount) }} {{ total.currencySymbol }}</strong>
                  </span>
                </footer>
              </article>
            </div>
          </template>
        </iq-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
  import Loader from '../../../components/shared/Loader'
  import { TOOTH_TREATMENT_PRICING_ENABLED, formatAmount, parseAmount, confirmToothAction } from '../../../components/tooth/toothConfig'
  import '../../../components/tooth/tooth-chip.css'
  import { replaceTurkishCharacters } from '../../../utils/stringExtensions'
  import '../../../plugins/sweetalert'
  import toothService from '../../../services/tooth'

  export default {
    name: 'SummaryToothTreatments',
    components: { Loader },
    props: {
      customerId: String
    },
    data() {
      return {
        isLoading: false,
        treatments: [],
        showPricing: TOOTH_TREATMENT_PRICING_ENABLED
      }
    },
    computed: {
      canCreate() { return this.$store.getters['Auth/checkPermission']('toothtreatment_new') },
      canEdit() { return this.$store.getters['Auth/checkPermission']('toothtreatment_edit') },
      canDelete() { return this.$store.getters['Auth/checkPermission']('toothtreatment_delete') },
      canDownload() { return this.$store.getters['Auth/checkPermission']('toothtreatment_download') }
    },
    methods: {
      formatAmount,
      formatDate(value) {
        return value ? this.$moment.parseZone(value).format('DD.MM.YYYY') : ''
      },
      formatQuantity(value) {
        const amount = parseAmount(value)
        return Number.isInteger(amount) ? amount : amount.toFixed(2)
      },
      getTreatments() {
        this.isLoading = true
        toothService.getCustomerToothTreatments(this.customerId)
          .then(response => { this.treatments = response || [] })
          .catch(() => { this.treatments = [] })
          .finally(() => { this.isLoading = false })
      },
      openTreatment(treatment) {
        if (!this.canEdit) return
        this.$router.push({ name: 'toothTreatment.edit', params: { toothTreatmentId: treatment.id, customerId: this.customerId } })
      },
      confirmDelete(treatment) {
        confirmToothAction(this, { text: this.$t('DeleteConfirmationText'), danger: true }).then(confirmed => {
          if (!confirmed) return

          toothService.deleteToothTreatment(treatment.id)
            .then(success => {
              if (success) {
                this.$toastr.success(this.$t('Success'))
                this.getTreatments()
              } else {
                this.$toastr.error(this.$t('Error'))
              }
            })
            .catch(() => { this.$toastr.error(this.$t('Error')) })
        })
      },
      download(treatment) {
        this.$toastr.warning(this.$t('FileDownloadStarted'))

        toothService.downloadToothTreatmentPdf(treatment.id)
          .then(response => {
            this.$toastr.remove()
            if (!response || !response.data || response.data.byteLength === 0) {
              this.$toastr.error(this.$t('Error'))
              return
            }

            let filename = ''
            const disposition = response.headers['content-disposition']
            if (disposition && disposition.indexOf('attachment') !== -1) {
              const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition)
              if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '')
            }
            filename = replaceTurkishCharacters(filename || `${this.$t('ToothTreatment')}-${this.formatDate(treatment.date)}.pdf`)

            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
            const link = document.createElement('a')
            link.href = url
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
            this.$toastr.success(this.$t('FileDownloadFinished'))
          })
          .catch(() => {
            this.$toastr.remove()
            this.$toastr.error(this.$t('Error'))
          })
      }
    },
    watch: {
      customerId() {
        this.getTreatments()
      }
    },
    mounted() {
      this.getTreatments()
    }
  }
</script>

<style scoped>
  .tooth-list__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 16px;
    color: #777d74;
    text-align: center;
  }

  .tooth-list__empty-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    margin-bottom: 12px;
    border-radius: 50%;
    font-size: 24px;
    color: #089bab;
    background: #e3f4f6;
  }

  .tooth-list__items {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .tooth-list__item {
    padding: 14px 16px;
    border: 1px solid #e6eeef;
    border-radius: 14px;
    background: #fff;
    transition: border-color .15s ease, box-shadow .15s ease;
  }

  .tooth-list__item--clickable {
    cursor: pointer;
  }

  .tooth-list__item--clickable:hover {
    border-color: rgba(8, 155, 171, .45);
    box-shadow: 0 6px 18px rgba(8, 155, 171, .10);
  }

  .tooth-list__item-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .tooth-list__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 14px;
    min-width: 0;
  }

  .tooth-list__date {
    font-weight: 600;
    color: #374948;
  }

  .tooth-list__muted {
    color: #8a9a99;
    font-size: 13px;
  }

  .tooth-list__actions {
    display: flex;
    align-items: center;
    gap: 2px;
    margin-left: auto;
  }

  .tooth-list__actions .btn-link {
    color: #777d74;
  }

  .tooth-list__actions .btn-link:hover {
    color: #089bab;
  }

  .tooth-list__actions .btn-link.text-danger:hover {
    color: #fd6256 !important;
  }

  .tooth-list__groups {
    list-style: none;
    margin: 10px 0 0;
    padding: 0;
  }

  .tooth-list__group {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    padding: 6px 0;
    border-top: 1px dashed #eef2f3;
  }

  .tooth-list__group:first-child {
    border-top: 0;
  }

  .tooth-list__chips {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px;
  }


  .tooth-list__entity {
    flex: 1 1 200px;
    min-width: 0;
    color: #374948;
  }

  .tooth-list__amount {
    font-weight: 600;
    color: #374948;
    white-space: nowrap;
  }

  .tooth-list__item-foot {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px 16px;
    margin-top: 8px;
    padding-top: 10px;
    border-top: 1px solid #eef2f3;
  }

  .tooth-list__note {
    flex: 1 1 280px;
    min-width: 0;
    color: #555f5e;
    font-size: 13px;
    white-space: pre-line;
    overflow-wrap: anywhere;
  }

  .tooth-list__totals {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 8px;
    margin-left: auto;
  }

  .tooth-list__totals strong {
    color: #089bab;
  }
</style>
